import { useState, useEffect, useMemo, useCallback } from "react";
import { observer } from "mobx-react-lite";
import { useStores } from "@/stores";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SelectSeparator,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { CreateCategoryDialog, CreateStageDialog } from "./TemplateLinkCreateDialogs";

/** Sentinel values for the "+ Create new…" entries in the two dropdowns. */
const NEW_CATEGORY = "__new_category__";
const NEW_STAGE = "__new_stage__";
/** Select cannot hold an empty string, so "no stage" needs a real value. */
const NO_STAGE = "__no_stage__";

const CATEGORY_MODES = [
  {
    value: "skip_llm",
    label: "Skip LLM",
    description: "The document gets only this category, no classification AI call",
  },
  {
    value: "additional",
    label: "Additional",
    description: "This category PLUS whatever the LLM returns",
  },
];

/** The editable state of one template's row. */
interface LinkDraft {
  category_id: number;
  category_mode: "skip_llm" | "additional";
  stage_id: number | null;
  feed_form_values: boolean;
}

const UNLINKED: LinkDraft = {
  category_id: 0,
  category_mode: "skip_llm",
  stage_id: null,
  feed_form_values: true,
};

/** Immutably add or drop an id from a set of in-flight writes. */
const withId = (ids: ReadonlySet<number>, id: number, inFlight: boolean): ReadonlySet<number> => {
  const next = new Set(ids);
  if (inFlight) {
    next.add(id);
  } else {
    next.delete(id);
  }
  return next;
};

/**
 * Templates Linking — ties each "New document" template to the classification its
 * documents are born with.
 *
 * One row per TEMPLATE rather than per link, so a template with nothing configured is
 * visible and can be given a link in place. There is deliberately no "create new" form
 * above the table: the template catalogue is fixed, and a link with no template would
 * mean nothing.
 */
const TemplateLinksTable = observer(() => {
  const { documentTemplatesStore, documentCategoriesStore, designStagesStore } = useStores();

  // Only the templates the admin has actually edited get an entry — everything else falls
  // back to the saved link, so reloading the links never discards another row's edits.
  const [drafts, setDrafts] = useState<Record<number, LinkDraft>>({});
  // The table's own errors: the store's `error` is set by writes too, so a failed save
  // would otherwise replace the whole table with a banner it could never clear.
  const [loadError, setLoadError] = useState<string | null>(null);
  // The store's `isLoading` is one flag shared by loadTemplates and loadLinks, each
  // clearing it in its own finally, so it goes false while the other call is still in
  // flight — the table would flash "No templates found." before the rows land.
  const [hasLoaded, setHasLoaded] = useState(false);
  const [optionsError, setOptionsError] = useState<string | null>(null);
  // Rows save independently, so the guard is a set and not one id: with a single id the
  // first write to finish clears the flag for a row whose own write is still in flight,
  // re-enabling its Save button — and a second click inserts the link twice, which the
  // unique constraint on template_id rejects with a raw Postgres error.
  const [savingTemplates, setSavingTemplates] = useState<ReadonlySet<number>>(new Set());
  const [deletingLinks, setDeletingLinks] = useState<ReadonlySet<number>>(new Set());
  const [searchQuery, setSearchQuery] = useState("");
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [linkToDelete, setLinkToDelete] = useState<{
    id: number;
    templateId: number;
    name: string;
  } | null>(null);

  // Which row opened a create dialog — the new row is auto-selected back into it.
  const [categoryDialogFor, setCategoryDialogFor] = useState<number | null>(null);
  const [stageDialogFor, setStageDialogFor] = useState<number | null>(null);

  useEffect(() => {
    const message = (error: unknown) => (error instanceof Error ? error.message : String(error));
    // Templates and links are the table itself — without them there is nothing to show.
    // Categories and stages only fill the dropdowns, so their failure is a warning, not a
    // blank screen; both must be reported or the admin sees empty dropdowns and no reason.
    Promise.all([documentTemplatesStore.loadTemplates(), documentTemplatesStore.loadLinks()])
      .catch((error) => setLoadError(message(error)))
      .finally(() => setHasLoaded(true));
    Promise.all([documentCategoriesStore.loadCategories(), designStagesStore.loadStages()]).catch(
      (error) => setOptionsError(message(error))
    );
  }, [documentTemplatesStore, documentCategoriesStore, designStagesStore]);

  const savedFor = useCallback(
    (templateId: number): LinkDraft => {
      const link = documentTemplatesStore.linkForTemplate(templateId);
      if (!link) return { ...UNLINKED };
      return {
        category_id: link.category_id,
        category_mode: link.category_mode as "skip_llm" | "additional",
        stage_id: link.stage_id,
        feed_form_values: link.feed_form_values,
      };
    },
    [documentTemplatesStore]
  );

  const hasChanges = useCallback(
    (templateId: number): boolean => {
      const draft = drafts[templateId];
      if (!draft) return false;
      const saved = savedFor(templateId);
      return (
        draft.category_id !== saved.category_id ||
        draft.category_mode !== saved.category_mode ||
        draft.stage_id !== saved.stage_id ||
        draft.feed_form_values !== saved.feed_form_values
      );
    },
    [drafts, savedFor]
  );

  const patchDraft = (templateId: number, patch: Partial<LinkDraft>) => {
    setDrafts((prev) => ({
      ...prev,
      [templateId]: { ...(prev[templateId] ?? savedFor(templateId)), ...patch },
    }));
  };

  /** Drop one template's edits, so its row follows the saved link again. */
  const clearDraft = (templateId: number) => {
    setDrafts((prev) => {
      if (!(templateId in prev)) return prev;
      const next = { ...prev };
      delete next[templateId];
      return next;
    });
  };

  /**
   * Categories that can only be assigned deterministically come first — those are the
   * ones that belong here. An ordinary category is still selectable: "link existing"
   * should not be artificially restricted.
   */
  const { autoAssignedCategories, otherCategories } = useMemo(() => {
    const all = documentCategoriesStore.categoriesList;
    return {
      autoAssignedCategories: all.filter((c) => c.category_type === "file-type"),
      otherCategories: all.filter((c) => c.category_type !== "file-type"),
    };
  }, [documentCategoriesStore.categoriesList]);

  const filteredTemplates = useMemo(() => {
    const templates = documentTemplatesStore.templatesList;
    if (!searchQuery.trim()) return templates;
    const query = searchQuery.toLowerCase();
    return templates.filter(
      (t) =>
        t.name.toLowerCase().includes(query) ||
        t.template_key.toLowerCase().includes(query) ||
        (t.name_et && t.name_et.toLowerCase().includes(query))
    );
  }, [documentTemplatesStore.templatesList, searchQuery]);

  const handleSave = async (templateId: number) => {
    const draft = drafts[templateId];
    if (!draft) return;
    if (!draft.category_id) {
      toast.error("A category is required");
      return;
    }

    setSavingTemplates((ids) => withId(ids, templateId, true));
    try {
      const existing = documentTemplatesStore.linkForTemplate(templateId);
      await documentTemplatesStore.saveLink(existing ? existing.id : null, {
        template_id: templateId,
        category_id: draft.category_id,
        category_mode: draft.category_mode,
        stage_id: draft.stage_id,
        feed_form_values: draft.feed_form_values,
      });
      clearDraft(templateId);
      toast.success("Template link saved");
    } catch (error) {
      toast.error(
        `Failed to save template link: ${error instanceof Error ? error.message : String(error)}`
      );
    } finally {
      setSavingTemplates((ids) => withId(ids, templateId, false));
    }
  };

  const handleDeleteClick = (templateId: number, templateName: string) => {
    const link = documentTemplatesStore.linkForTemplate(templateId);
    if (!link) return;
    setLinkToDelete({ id: link.id, templateId, name: templateName });
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    const target = linkToDelete;
    if (!target) return;
    // The dialog closes on the confirm click, so a second removal can be started while
    // this one is still running. Close it up front and key everything to the link that
    // was confirmed, so a slow delete cannot close the dialog a later one opened.
    setDeleteConfirmOpen(false);
    setDeletingLinks((ids) => withId(ids, target.id, true));
    try {
      await documentTemplatesStore.deleteLink(target.id);
      clearDraft(target.templateId);
      toast.success("Template link removed");
    } catch (error) {
      toast.error(
        `Failed to remove template link: ${error instanceof Error ? error.message : String(error)}`
      );
    } finally {
      setDeletingLinks((ids) => withId(ids, target.id, false));
    }
  };

  if (!hasLoaded) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="rounded-md bg-destructive/10 p-4 text-destructive">
        Error loading template links: {loadError}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {optionsError && (
        <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
          The category and design stage lists could not be loaded, so the dropdowns are empty:{" "}
          {optionsError}
        </div>
      )}

      <Input
        placeholder="Search templates..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        className="max-w-sm"
      />

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[18%]">Template</TableHead>
            <TableHead className="w-[22%]">Category</TableHead>
            <TableHead className="w-[16%]">Mode</TableHead>
            <TableHead className="w-[22%]">Design stage</TableHead>
            <TableHead className="w-[12%]">Form answers to LLM</TableHead>
            <TableHead className="w-[10%] text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {filteredTemplates.length === 0 && (
            <TableRow>
              <TableCell colSpan={6} className="py-6 text-center text-muted-foreground">
                No templates found.
              </TableCell>
            </TableRow>
          )}

          {filteredTemplates.map((template) => {
            const draft = drafts[template.id] ?? savedFor(template.id);
            const link = documentTemplatesStore.linkForTemplate(template.id);
            const changed = hasChanges(template.id);
            // The dropdown lists top-level categories only, so a link pointing at a
            // subcategory has no matching item and the Select would fall back to its
            // placeholder — a configured row would look unconfigured. Label it from the
            // name the link already carries.
            const unlistedCategoryLabel =
              draft.category_id && !documentCategoriesStore.getById(draft.category_id)
                ? (link?.category_name ?? `Category #${draft.category_id}`)
                : undefined;

            return (
              <TableRow key={template.id}>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{template.name}</span>
                    {!template.is_active && (
                      <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] uppercase text-muted-foreground">
                        Inactive
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground">{template.name_et}</div>
                  <code className="text-[10px] text-muted-foreground">{template.template_key}</code>
                  {/* The pipeline reads links for active templates only, so an editable
                      row on an inactive template would silently do nothing. */}
                  {!template.is_active && (
                    <div className="mt-1 text-[10px] text-amber-600">
                      Inactive template — a link saved here is not applied until the template is
                      active again.
                    </div>
                  )}
                </TableCell>

                <TableCell>
                  <Select
                    value={draft.category_id ? String(draft.category_id) : ""}
                    onValueChange={(value) => {
                      if (value === NEW_CATEGORY) {
                        setCategoryDialogFor(template.id);
                      } else {
                        patchDraft(template.id, { category_id: Number(value) });
                      }
                    }}
                  >
                    <SelectTrigger className="h-8 w-full">
                      <SelectValue placeholder="Select category">
                        {unlistedCategoryLabel}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NEW_CATEGORY} className="font-medium text-primary">
                        + Create New Category...
                      </SelectItem>
                      <SelectSeparator />
                      {autoAssignedCategories.map((cat) => (
                        <SelectItem key={cat.id} value={String(cat.id)}>
                          {cat.category_name}
                        </SelectItem>
                      ))}
                      {autoAssignedCategories.length > 0 && otherCategories.length > 0 && (
                        <SelectSeparator />
                      )}
                      {otherCategories.map((cat) => (
                        <SelectItem key={cat.id} value={String(cat.id)}>
                          {cat.category_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </TableCell>

                <TableCell>
                  <Select
                    value={draft.category_mode}
                    onValueChange={(value) =>
                      patchDraft(template.id, {
                        category_mode: value as "skip_llm" | "additional",
                      })
                    }
                  >
                    <SelectTrigger className="h-8 w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORY_MODES.map((mode) => (
                        <SelectItem key={mode.value} value={mode.value}>
                          <div>
                            <div>{mode.label}</div>
                            <div className="text-xs text-muted-foreground">{mode.description}</div>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </TableCell>

                <TableCell>
                  <Select
                    value={draft.stage_id ? String(draft.stage_id) : NO_STAGE}
                    onValueChange={(value) => {
                      if (value === NEW_STAGE) {
                        setStageDialogFor(template.id);
                      } else {
                        patchDraft(template.id, {
                          stage_id: value === NO_STAGE ? null : Number(value),
                        });
                      }
                    }}
                  >
                    <SelectTrigger className="h-8 w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NEW_STAGE} className="font-medium text-primary">
                        + Create New Stage...
                      </SelectItem>
                      <SelectSeparator />
                      <SelectItem value={NO_STAGE}>
                        <div>
                          <div>Classify with the LLM</div>
                          <div className="text-xs text-muted-foreground">No stage tied</div>
                        </div>
                      </SelectItem>
                      <SelectSeparator />
                      {designStagesStore.stagesList.map((stage) => (
                        <SelectItem key={stage.id} value={String(stage.id)}>
                          {stage.name}
                          {stage.name_en ? ` (${stage.name_en})` : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </TableCell>

                <TableCell>
                  <Checkbox
                    checked={draft.feed_form_values}
                    onCheckedChange={(checked) =>
                      patchDraft(template.id, { feed_form_values: checked === true })
                    }
                    aria-label="Feed the form's answers to the discipline classifier"
                  />
                </TableCell>

                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={!changed || savingTemplates.has(template.id)}
                      onClick={() => handleSave(template.id)}
                    >
                      {savingTemplates.has(template.id) ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        "Save"
                      )}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={!link || deletingLinks.has(link.id)}
                      onClick={() => handleDeleteClick(template.id, template.name)}
                      aria-label={`Remove the link for ${template.name}`}
                    >
                      {link && deletingLinks.has(link.id) ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Trash2 className="h-4 w-4 text-destructive" />
                      )}
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      <CreateCategoryDialog
        open={categoryDialogFor !== null}
        onOpenChange={(open) => {
          if (!open) setCategoryDialogFor(null);
        }}
        onCreated={(categoryId) => {
          if (categoryDialogFor !== null)
            patchDraft(categoryDialogFor, { category_id: categoryId });
          setCategoryDialogFor(null);
        }}
      />

      <CreateStageDialog
        open={stageDialogFor !== null}
        onOpenChange={(open) => {
          if (!open) setStageDialogFor(null);
        }}
        onCreated={(stageId) => {
          if (stageDialogFor !== null) patchDraft(stageDialogFor, { stage_id: stageId });
          setStageDialogFor(null);
        }}
      />

      <ConfirmDialog
        open={deleteConfirmOpen}
        onOpenChange={setDeleteConfirmOpen}
        title="Remove template link"
        description={
          linkToDelete
            ? `Documents generated from "${linkToDelete.name}" will be classified by the LLM like any other document. Existing documents keep the categories they already have.`
            : ""
        }
        confirmText="Remove"
        onConfirm={handleDeleteConfirm}
      />
    </div>
  );
});

export default TemplateLinksTable;
